import React, { useState, useEffect } from 'react';
import { FunnelIcon, XMarkIcon } from '@/components/Icons';
import { NeighborhoodMultiSelect } from '@/components/public/NeighborhoodMultiSelect';

interface LocationFiltersProps {
  neighborhoods: string[];
  selectedNeighborhoods: string[];
  onNeighborhoodChange: (selected: string[]) => void;
  priceRange: { min: string; max: string };
  onPriceRangeChange: (range: { min: string; max: string }) => void;
  onClear: () => void;
  onClose?: () => void;
  resultsCount: number;
}

export const LocationFilters: React.FC<LocationFiltersProps> = ({
  neighborhoods,
  selectedNeighborhoods,
  onNeighborhoodChange,
  priceRange,
  onPriceRangeChange,
  onClear,
  onClose,
  resultsCount
}) => {
  // Local state for inputs to avoid focus loss and allow smooth typing
  const [localMin, setLocalMin] = useState(priceRange.min);
  const [localMax, setLocalMax] = useState(priceRange.max);

  const formatNumber = (val: string) => {
    if (!val) return '';
    const num = val.replace(/\D/g, '');
    return new Intl.NumberFormat('es-CO').format(Number(num));
  };

  const handleMinChange = (val: string) => {
    const rawValue = val.replace(/\D/g, '');
    setLocalMin(rawValue);
    onPriceRangeChange({ ...priceRange, min: rawValue });
  };

  const handleMaxChange = (val: string) => {
    const rawValue = val.replace(/\D/g, '');
    setLocalMax(rawValue);
    onPriceRangeChange({ ...priceRange, max: rawValue });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between lg:hidden">
        <h2 className="text-lg font-bold">Filtros</h2>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
          <XMarkIcon className="w-6 h-6 text-gray-500" />
        </button>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Barrios</label>
        <NeighborhoodMultiSelect 
          options={neighborhoods} 
          selected={selectedNeighborhoods} 
          onChange={onNeighborhoodChange} 
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1 text-nowrap">Precio Mín</label>
          <div className="relative flex items-center">
            <span className="absolute left-3 text-sm font-semibold text-gray-400 pointer-events-none">$</span>
            <input 
              type="text" 
              inputMode="numeric"
              value={formatNumber(localMin)} 
              onChange={(e) => handleMinChange(e.target.value)} 
              placeholder="0" 
              className="w-full h-11 pl-7 pr-3 py-2 bg-white rounded-xl border border-gray-300 text-sm font-medium text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 shadow-sm" 
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1 text-nowrap">Precio Máx</label>
          <div className="relative flex items-center">
            <span className="absolute left-3 text-sm font-semibold text-gray-400 pointer-events-none">$</span>
            <input 
              type="text" 
              inputMode="numeric"
              value={formatNumber(localMax)} 
              onChange={(e) => handleMaxChange(e.target.value)} 
              placeholder="0" 
              className="w-full h-11 pl-7 pr-3 py-2 bg-white rounded-xl border border-gray-300 text-sm font-medium text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 shadow-sm" 
            />
          </div>
        </div>
      </div>

      <div className="pt-2 space-y-3">
        <button 
          onClick={onClear} 
          className="w-full py-2 text-sm text-sky-600 font-medium hover:bg-sky-50 rounded-lg transition-colors"
        >
          Limpiar filtros
        </button>
        
        {onClose && (
          <button 
            onClick={onClose} 
            className="w-full py-3 bg-sky-600 text-white font-bold rounded-lg lg:hidden shadow-md active:scale-95 transition-transform"
          >
            Ver {resultsCount} resultados
          </button>
        )}
      </div>
    </div>
  );
};
